import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WorkspacePremium as SeniorityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { seniorityLevelService } from '../services/api';
import './SeniorityLevelManagement.css';
import { useConfirm } from './Common/ConfirmDialog';

const SeniorityLevelManagement = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    level: 1,
    description: ''
  });

  // Load seniority levels
  useEffect(() => {
    loadSeniorityLevels();
  }, []);

  const loadSeniorityLevels = async () => {
    try {
      setLoading(true);
      const data = await seniorityLevelService.getAll();

      console.log('API Response:', data); // Debug log

      // Ensure data is an array
      if (Array.isArray(data)) {
        setSeniorityLevels(data);
      } else {
        console.warn('API returned non-array data:', data);
        setSeniorityLevels([]);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load seniority levels');
      console.error('Error loading seniority levels:', err);
      setSeniorityLevels([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    console.log('handleAdd called - clearing editingItem');
    setEditingItem(null);
    setFormData({
      code: '',
      name: '',
      level: seniorityLevels.length + 1,
      description: ''
    });
    setDialogOpen(true);
    console.log('handleAdd completed - editingItem should be null');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      level: item.level || 1,
      description: item.description || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this seniority level?', danger: true, confirmLabel: 'Delete' }))) {
      return;
    }

    try {
      await seniorityLevelService.delete(id);
      setSuccess('Seniority level deleted successfully');
      loadSeniorityLevels();
    } catch (err) {
      setError('Failed to delete seniority level');
      console.error('Error deleting seniority level:', err);
    }
  };

  const handleSave = async () => {
    try {
      console.log('handleSave called');
      console.log('editingItem:', editingItem);
      console.log('Saving seniority level with data:', formData);

      if (editingItem) {
        console.log('Updating existing item with ID:', editingItem.id);
        await seniorityLevelService.update(editingItem.id, formData);
        setSuccess('Seniority level updated successfully');
      } else {
        console.log('Creating new seniority level');
        const response = await seniorityLevelService.create(formData);
        console.log('Create response:', response);
        setSuccess('Seniority level created successfully');
      }

      setDialogOpen(false);
      loadSeniorityLevels();
    } catch (err) {
      setError('Failed to save seniority level');
      console.error('Error saving seniority level:', err);
      console.error('Error details:', err.response?.data);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getLevelColor = (level) => {
    const colors = ['#f44336', '#ff9800', '#2196f3', '#4caf50', '#9c27b0'];
    return colors[level % colors.length] || '#757575';
  };

  return (
    <Box className="seniority-management-container">
      {/* Header with Breadcrumbs */}
      <Box className="seniority-header">
        <Breadcrumbs className="breadcrumbs">
          <Link 
            color="inherit" 
            onClick={() => navigate('/settings')}
            className="breadcrumb-link"
          >
            Settings
          </Link>
          <Typography color="text.primary">Seniority Levels</Typography>
        </Breadcrumbs>
        
        <Box className="header-content">
          <Box className="title-section">
            <SeniorityIcon className="header-icon" />
            <Box>
              <Typography variant="h4" className="page-title">
                Seniority Levels
              </Typography>
              <Typography variant="body1" className="page-subtitle">
                Manage employee seniority levels and career progression
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className="add-button"
          >
            Add Seniority Level
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} className="alert">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} className="alert">
          {success}
        </Alert>
      )}

      {/* Content */}
      <Card className="content-card">
        <CardContent>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !Array.isArray(seniorityLevels) || seniorityLevels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No seniority levels found
                    </TableCell>
                  </TableRow>
                ) : (
                  seniorityLevels.map((item) => (
                    <TableRow key={item.id} className="table-row">
                      <TableCell>
                        <Chip
                          label={item.level || 1}
                          size="small"
                          style={{
                            backgroundColor: getLevelColor(item.level || 1),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="code-cell">
                          {item.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" className="name-cell">
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="description-cell">
                          {item.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.employeeCount || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          className="action-button edit-button"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          className="action-button delete-button"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Seniority Level' : 'Add New Seniority Level'}
        </DialogTitle>
        <DialogContent>
          <Box className="dialog-form">
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Level"
              type="number"
              value={formData.level}
              onChange={(e) => handleInputChange('level', parseInt(e.target.value) || 1)}
              fullWidth
              margin="normal"
              inputProps={{ min: 1, max: 10 }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeniorityLevelManagement;
